/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B020020 売上照会
 */
import { Column, Query } from "@material-table/core";
import {
  B000120EigyoNmr,
  B000150TokuiNmr,
  B020020Delete,
  B020020DeleteRequest,
  B020020Find,
  B020020FindRequest,
  B020020Get,
  B020020GetCopy,
  B020020GetCopyRequest,
  B020020TogetsuYm,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SDateField,
  SFrame,
  SLabel,
  SMultiDialog,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import { B020020SyuturyokuKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { B000120, B000130, B000140, B000150, B000202 } from "pages";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { mapIds } from "services";
import { editKbn } from "services/codes";
import { date, dateWithSlashAt } from "services/DateService";
import { authState, focusState, navigateTypeState, useFocus } from "states";
import { B020010InputData } from "./B020010";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  uriYmdFrom: string;
  uriYmdTo: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  uridenpyoNoFrom: string;
  uridenpyoNoTo: string;
  juchudenpyoNoFrom: string;
  juchudenpyoNoTo: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNmr: string;
  eigyoCd: string;
  eigyoNmr: string;
  shainCdFrom: string;
  shainCdTo: string;
  senyodenpyoNo: string;
  chumonNo: string;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  shutsuryokujun: SCode;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  uriYmd: string;
  uridenpyoNo: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNm: string;
  senyodenpyoNo: string;
  chumonNo: string;
  shainNm: string;
  uriKin: number;
  genkaKin: number;
  bumonNm: string;
  arariKin: number;
  uridenpyohakozumiKbn: string;
  shukajokyo: string;
  isSansho: boolean;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  uriYmdFrom: "",
  uriYmdTo: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  uridenpyoNoFrom: "",
  uridenpyoNoTo: "",
  juchudenpyoNoFrom: "",
  juchudenpyoNoTo: "",
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNmr: "",
  eigyoCd: "",
  eigyoNmr: "",
  shainCdFrom: "",
  shainCdTo: "",
  senyodenpyoNo: "",
  chumonNo: "",
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  shutsuryokujun: getSCode(B020020SyuturyokuKbn, "1"),
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行", width: "4rem" },
  {
    field: "uriYmd",
    title: "売上日",
    width: "6rem",
    render: (data) => {
      if (data.uriYmd! !== null && data.uriYmd! !== "") {
        return dateWithSlashAt(data.uriYmd!);
      }
    },
  },
  { field: "uridenpyoNo", title: "売上No", width: "6rem" },
  { field: "tokuiNm", title: "得意先", width: "15rem" },
  { field: "senyodenpyoNo", title: "専用伝票No", width: "6rem" },
  { field: "chumonNo", title: "客先No", width: "7rem" },
  { field: "shainNm", title: "売上担当者", width: "8rem" },
  {
    field: "uriKin",
    title: "売上金額　",
    width: "5rem",
    align: "right",
    render: (data) => data.uriKin?.toLocaleString(),
  },
  {
    field: "genkaKin",
    title: "原価金額　",
    width: "5rem",
    align: "right",
    render: (data) => data.genkaKin?.toLocaleString(),
  },
  { field: "bumonNm", title: "売上担当部門", width: "6rem" },
  {
    field: "arariKin",
    title: "粗利金額　",
    width: "5rem",
    align: "right",
    render: (data) => data.arariKin?.toLocaleString(),
  },
  {
    field: "uridenpyohakozumiKbn",
    title: "伝票発行",
    width: "5rem",
  },
  {
    field: "shukajokyo",
    title: "出荷状況",
    width: "5rem",
  },
  { field: "tokuiCd", hidden: true },
  { field: "tokuishitenCd", hidden: true },
  { field: "isSansho", hidden: true },
];

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
}>;

// 検索条件を格納するステート
export const B020020State = atom<SearchedFields>({
  key: "B020020State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

// フォーカスIDの設定
const focusId = "B020020";

export const B020020 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  const [searchedFeields, setSearchedFields] = useRecoilState(B020020State);

  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();

  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);

  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);

  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移の処理
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);

  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // 所属営業所コードでの営業所コード入力制御

  // 初期表示イベント
  useEffect(() => {
    if (menuNavigate === "menu") {
      // 通常の初期表示時の処理を記載する。

      // 初期表示
      (async () => {
        await methods.setValue("eigyoCd", auth?.eigyoCd!);

        await B000120EigyoNmr(methods, navigate, setMsg);
      })();

      // 初回フォーカス先
      // システムマスタの当月の初日
      (async () => {
        await B020020TogetsuYm(methods, navigate, setMsg);
        methods.setValue("uriYmdTo", date());
        focus(1);
      })();
      return;
    }

    if (searchedFeields.searched) {
      // 検索条件が保存されている
      const fields = searchedFeields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("uriYmdFrom", fields.uriYmdFrom);
        methods.setValue("uriYmdTo", fields.uriYmdTo);
        methods.setValue("bumonCdFrom", fields.bumonCdFrom);
        methods.setValue("bumonCdTo", fields.bumonCdTo);
        methods.setValue("uridenpyoNoFrom", fields.uridenpyoNoFrom);
        methods.setValue("uridenpyoNoTo", fields.uridenpyoNoTo);
        methods.setValue("juchudenpyoNoFrom", fields.juchudenpyoNoFrom);
        methods.setValue("juchudenpyoNoTo", fields.juchudenpyoNoTo);
        methods.setValue("tokuiCd", fields.tokuiCd);
        methods.setValue("tokuishitenCd", fields.tokuishitenCd);
        methods.setValue("tokuiNmr", fields.tokuiNmr);
        methods.setValue("eigyoCd", fields.eigyoCd);
        methods.setValue("eigyoNmr", fields.eigyoNmr);
        methods.setValue("shainCdFrom", fields.shainCdFrom);
        methods.setValue("shainCdTo", fields.shainCdTo);
        methods.setValue("senyodenpyoNo", fields.senyodenpyoNo);
        methods.setValue("chumonNo", fields.chumonNo);
        methods.setValue("shodaibun", fields.shodaibun);
        methods.setValue("shochubun", fields.shochubun);
        methods.setValue("shoshobun", fields.shoshobun);
        methods.setValue("shutsuryokujun", fields.shutsuryokujun);
        setSearched(true);
        return;
      }
    }
  }, []);

  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();

  // selectboxで選択された行のデータ
  const [selectedData, setSelectedData] = useState<Data[]>([]);

  // ボタンの活性・非活性
  const [disabledUriageCancel, setDisabledUriageCancel] = useState(true);
  const [disabledUriageCopy, setDisabledUriageCopy] = useState(true);
  const [disabledUriageDetail, setDisabledUriageDetail] = useState(true);

  // テーブル行選択時
  const handleSelectTableRow = (_: any, rowData: Data | undefined) => {
    // 行が選択されていないときはボタン非活性
    const disabled = typeof rowData === "undefined";
    setDisabledUriageCopy(disabled);
    setDisabledUriageDetail(disabled);

    // データを保存
    setRowData(rowData);
  };

  // テーブル行ダブルクリック時
  const handleDoubleClickTableRow = (data: Data) => {
    // 売上データの検索を行う
    B020020Get({ uridenpyoNo: data.uridenpyoNo })
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.reference,
          uriage: value.data,
        };
        // 参照モードフラグがtrueの場合は参照
        if (inputData.uriage.isSansho === false) {
          inputData.editKbn = editKbn.update;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 売上入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  //詳細ボタンクリック時
  const handleShowDetail = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }

    // 売上データの検索を行う
    B020020Get({ uridenpyoNo: rowData.uridenpyoNo })
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.reference,
          uriage: value.data,
        };
        // 参照モードフラグがtrueの場合は参照
        if (inputData.uriage.isSansho === false) {
          inputData.editKbn = editKbn.update;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 売上入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 複製ボタンクリック時 「売上ヘッダのみ」を選択した場合
  const handleClickHeadOnly = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }

    // 選択行の 「uridenpyoNo」と「copyKbn = 1」(ヘッダのみ) をパラメータに/b020020/get/copy を呼び出す
    const param: B020020GetCopyRequest = {
      uridenpyoNo: rowData.uridenpyoNo,
      copyKbn: "1",
    };
    B020020GetCopy(param)
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.register,
          uriage: value.data,
        };
        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 売上入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 複製ボタンクリック時 「売上ヘッダ + 売上明細」を選択した場合
  const handleClickAll = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }

    // 選択行の 「uridenpyoNo」と「copyKbn = 0」(すべて) をパラメータに/b020020/get/copy を呼び出す。
    const param: B020020GetCopyRequest = {
      uridenpyoNo: rowData.uridenpyoNo,
      copyKbn: "0",
    };
    B020020GetCopy(param)
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.register,
          uriage: value.data,
        };
        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 売上入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行チェックボックス選択時
  const handleCheckTableRow = (data: Data[]) => {
    // 行が選択されている場合のみ実行する。
    const disabled = data.length <= 0;
    setDisabledUriageCancel(disabled);
    setSelectedData(data);
  };

  // 売上削除イベント
  const handleCancel = () => {
    // リクエスト作成
    const param: B020020DeleteRequest = {
      uridenpyoNoList: selectedData.map((value) => {
        return {
          uridenpyoNo: value.uridenpyoNo,
        };
      }),
    };
    // API呼び出し
    B020020Delete(param)
      .then(() => {
        // 削除後に先頭ページに戻る
        isDefaultPage.current = true;
        // テーブル再検索
        (tableRef.current as any).onQueryChange();
        // 各ボタン非活性化
        setDisabledUriageCancel(true);
        setDisabledUriageCopy(true);
        setDisabledUriageDetail(true);
        // メッセージ設定
        setMsg(getClientMessage("B020020_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // ボタン非活性化
    setDisabledUriageCancel(true);
    setDisabledUriageCopy(true);
    setDisabledUriageDetail(true);
    // 選択されているデータを空にする
    setRowData({});
    setSelectedData([]);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順の値に応じたソート処理
      let order;
      if (fields.shutsuryokujun?.value === "1") {
        order = ["uriYmd,asc", "uridenpyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "2") {
        order = ["tokuiCd,asc", "tokuishitenCd,asc", "uridenpyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "3") {
        order = ["uridenpyoNo,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B020020FindRequest = {
        uriYmdFrom: fields.uriYmdFrom,
        uriYmdTo: fields.uriYmdTo,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        uridenpyoNoFrom: fields.uridenpyoNoFrom,
        uridenpyoNoTo: fields.uridenpyoNoTo,
        juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
        juchudenpyoNoTo: fields.juchudenpyoNoTo,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        eigyoCd: fields.eigyoCd,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        senyodenpyoNo: fields.senyodenpyoNo,
        chumonNo: fields.chumonNo,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: order,
      };
      // API呼び出し
      B020020Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="売上照会"
        gridTemplateColumns="6rem 5rem 6rem 1rem 11rem 6rem 3rem 3rem 5rem 1rem 12rem"
      >
        <SLabel>売上日</SLabel>
        <SDateField name="uriYmdFrom" {...register(1)} gridColumn="span 2" />
        <SText>～</SText>
        <SDateField name="uriYmdTo" {...register(2)} />
        <SLabel required>営業所</SLabel>
        <STextField
          required
          label="営業所コード"
          name="eigyoCd"
          {...register(12)}
          size={6}
          gridColumn="span 3"
          dialog={<B000120 />}
          handleSubmit={(data: any) => {
            methods.setValue("eigyoCd", data.eigyoCd);
            methods.setValue("eigyoNmr", data.eigyoNm);
          }}
          onBlur={() => B000120EigyoNmr(methods, navigate, setMsg)}
        />

        <STextField name="eigyoNmr" gridColumn="span 2" disabled />
        <SLabel gridColumn="1">売上担当部門</SLabel>
        <SNumberField
          name="bumonCdFrom"
          code
          size={6}
          {...register(3)}
          gridColumn="span 2"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
          }}
        />
        <SText>～</SText>
        <SNumberField
          name="bumonCdTo"
          code
          size={6}
          {...register(4)}
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
          }}
        />
        <SLabel>売上担当者</SLabel>
        <SNumberField
          name="shainCdFrom"
          code
          size={7}
          {...register(13)}
          gridColumn="span 3"
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
          }}
        />
        <SText>～</SText>
        <SNumberField
          name="shainCdTo"
          code
          size={7}
          {...register(14)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
          }}
        />
        <SLabel gridColumn="1">売上No</SLabel>
        <SNumberField
          name="uridenpyoNoFrom"
          code
          size={10}
          {...register(5)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SNumberField name="uridenpyoNoTo" code size={10} {...register(6)} />
        <SLabel>専用伝票No</SLabel>
        <STextField
          name="senyodenpyoNo"
          size={10}
          {...register(15)}
          gridColumn="span 3"
        />
        <SLabel gridColumn="1">受注No</SLabel>
        <SNumberField
          name="juchudenpyoNoFrom"
          code
          size={10}
          {...register(7)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SNumberField name="juchudenpyoNoTo" code size={10} {...register(8)} />
        <SLabel>客先No</SLabel>
        <SNumberField
          name="chumonNo"
          code
          size={11}
          {...register(16)}
          gridColumn="span 3"
        />
        <SLabel gridColumn="1">得意先</SLabel>
        <SNumberField
          name="tokuiCd"
          code
          size={6}
          {...register(9)}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
        />
        <SNumberField
          name="tokuishitenCd"
          code
          size={4}
          {...register(10)}
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNmr", data.tokuiNmr);
          }}
        />
        <STextField name="tokuiNmr" size={40} disabled gridColumn="span 2" />
        <SLabel>商品分類</SLabel>
        <SNumberField name="shodaibun" code size={3} {...register(17)} />
        <SNumberField name="shochubun" code size={3} {...register(18)} />
        <SNumberField
          name="shoshobun"
          code
          size={3}
          {...register(19)}
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            if (data.shochubun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", "");
              methods.setValue("shoshobun", "");
            } else if (data.shoshobun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", "");
            } else {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", data.shoshobun);
            }
          }}
        />
        <SLabel gridColumn={1}>出力順</SLabel>
        <SCodeSelect
          name="shutsuryokujun"
          options={B020020SyuturyokuKbn}
          {...register(11)}
          gridColumn="span 2"
        />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          gridRow={8}
          gridColumn={11}
          {...register(20)}
          align="right"
        />
      </SFrame>
      <SFrame>
        <SText></SText>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          onRowClick={handleSelectTableRow}
          onSelectionChange={handleCheckTableRow}
          width={1280}
          height={340}
          multi
          handleSetDisabled={(rowData) => rowData.isSansho === true}
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame gridTemplateColumns="7rem 64.8rem 4rem 4rem">
        {" "}
        <SCancelButton
          label={labels.buttons.deleteUriage}
          gridColumn={1}
          {...register(21)}
          disabled={disabledUriageCancel}
          dialog={
            <SConfirmDialog
              title="売上データ削除"
              msg="選択中の売上データを削除します。よろしいですか？"
            />
          }
          dialogAction="confirm"
          handleSubmit={handleCancel}
        />
        <SNormalButton
          label={labels.buttons.copy}
          gridColumn={3}
          {...register(22)}
          disabled={disabledUriageCopy}
          dialog={
            <SMultiDialog
              title="売上データ複製"
              msg="新規作成する売上データに引き継ぐ対象を選択してください。"
              events={[
                {
                  label: "売上ヘッダのみ",
                  handleSubmit: handleClickHeadOnly,
                },
                {
                  label: "売上ヘッダ+売上明細",
                  handleSubmit: handleClickAll,
                },
              ]}
            />
          }
          dialogAction="close"
        />
        <SNormalButton
          label={labels.buttons.detail}
          gridColumn={4}
          {...register(23)}
          onClick={handleShowDetail}
          disabled={disabledUriageDetail}
          align="right"
        />
      </SFrame>
    </FormProvider>
  );
};
